import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Download, Loader2, Calendar, MapPin, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { ticketsService } from '../services/ticketsService';
import { generateTicketPDF } from '../utils/ticketGenerator';
import Button from '../components/ui/Button';
import TicketCard from '../components/TicketCard';
import Footer from '../components/Footer';

const Tickets: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { openLogin } = useAuthModal();
  const [tickets, setTickets] = useState<any[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'valid' | 'used' | 'cancelled'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'upcoming' | 'past'>('all');

  useEffect(() => {
    if (isAuthenticated && user) {
      loadTickets();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchQuery, statusFilter, dateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTickets = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const ticketsData = await ticketsService.getTickets();
      
      // Transform tickets for TicketCard component
      const transformed = ticketsData.map(ticket => ({
        id: ticket.id,
        eventId: ticket.event.id,
        eventTitle: ticket.event.title,
        eventImage: ticket.event.image,
        date: ticket.event.date,
        time: ticket.event.time,
        venue: ticket.event.venue,
        seat: ticket.seatInfo,
        qrCode: ticket.qrCode,
        ticketType: ticket.ticketType,
        price: ticket.price,
        status: ticket.status,
        ticketNumber: ticket.ticketNumber
      }));
      
      setTickets(transformed);
    } catch (err: any) {
      console.error('Error loading tickets:', err);
      setError(err.message || 'Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = [...tickets];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.eventTitle.toLowerCase().includes(query) ||
        ticket.venue.toLowerCase().includes(query) ||
        ticket.ticketType.toLowerCase().includes(query) ||
        ticket.ticketNumber.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(ticket => {
        const eventDate = new Date(ticket.date);
        if (dateFilter === 'upcoming') {
          return eventDate >= now;
        } else {
          return eventDate < now;
        }
      });
    }

    setFilteredTickets(filtered);
  };

  const handleDownloadTicket = async (ticketId: string) => {
    try {
      const ticket = tickets.find(t => t.id === ticketId);
      if (!ticket) {
        alert('Ticket not found');
        return;
      }

      const ticketData = {
        ticketNumber: ticket.ticketNumber,
        eventTitle: ticket.eventTitle,
        eventDate: ticket.date,
        eventTime: ticket.time,
        eventLocation: ticket.venue,
        eventVenue: ticket.venue,
        category: 'Event',
        ticketType: ticket.ticketType || ticket.ticketType,
        price: ticket.price,
        customerName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Guest',
        customerPhone: user?.phone || '',
        seatInfo: ticket.seat,
        qrCode: ticket.qrCode
      };

      const pdfBlob = await generateTicketPDF(ticketData);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ticket-${ticket.ticketNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading ticket:', err);
      alert('Failed to download ticket. Please try again.');
    }
  };

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Liquid Glass Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
      </div>

      <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-24 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 liquid-stagger-children">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 liquid-glow-strong" style={{color: 'var(--liquid-text-primary)'}}>
              {t('common.tickets') || 'My Tickets'}
            </h1>
            <p className="text-lg max-w-2xl mx-auto leading-relaxed" style={{color: 'var(--liquid-text-muted)'}}>
              {t('tickets.subtitle') || 'Your purchased event tickets'}
            </p>
          </div>

          {/* Filters and Search */}
          {isAuthenticated && (
            <div className="mb-8 glass-card rounded-2xl p-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 opacity-50" size={20} style={{color: 'var(--liquid-text-primary)'}} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tickets by event, venue, or ticket number..."
                    className="w-full pl-12 pr-4 py-3 glass-input rounded-xl focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                  />
                </div>

                {/* Status Filter */}
                <div className="flex items-center space-x-2">
                  <Filter size={20} className="opacity-50" style={{color: 'var(--liquid-text-primary)'}} />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="px-4 py-3 glass-input rounded-xl focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                  >
                    <option value="all">All Status</option>
                    <option value="valid">Valid</option>
                    <option value="used">Used</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Date Filter */}
                <div className="flex items-center space-x-2">
                  <Calendar size={20} className="opacity-50" style={{color: 'var(--liquid-text-primary)'}} />
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value as any)}
                    className="px-4 py-3 glass-input rounded-xl focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                  >
                    <option value="all">All Dates</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="past">Past</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 size={48} className="animate-spin text-blue-400" />
            </div>
          ) : error ? (
            /* Error State */
            <div className="text-center py-16">
              <div className="w-24 h-24 liquid-glass rounded-full flex items-center justify-center mx-auto mb-6">
                <Download size={32} className="liquid-glow text-red-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4 liquid-glow" style={{color: 'var(--liquid-text-primary)'}}>Error loading tickets</h3>
              <p className="mb-8 max-w-md mx-auto" style={{color: 'var(--liquid-text-muted)'}}>
                {error}
              </p>
              <Button onClick={loadTickets}>Try Again</Button>
            </div>
          ) : !isAuthenticated ? (
            /* Not Authenticated State */
            <div className="text-center py-16">
              <div className="w-24 h-24 liquid-glass rounded-full flex items-center justify-center mx-auto mb-6">
                <Download size={32} className="liquid-glow" style={{color: 'var(--liquid-text-muted)'}} />
              </div>
              <h3 className="text-2xl font-bold mb-4 liquid-glow" style={{color: 'var(--liquid-text-primary)'}}>Sign in to view tickets</h3>
              <p className="mb-8 max-w-md mx-auto" style={{color: 'var(--liquid-text-muted)'}}>
                Please sign in to view your purchased tickets.
              </p>
              <Button onClick={openLogin}>Sign In</Button>
            </div>
          ) : filteredTickets.length > 0 ? (
            /* Tickets list */
            <div className="space-y-8">
              {filteredTickets.map((ticket) => (
                <div key={ticket.id} className="glass-card rounded-2xl p-6">
                  <TicketCard ticket={ticket} />
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/10">
                    <div className="flex items-center space-x-4 text-sm opacity-70" style={{color: 'var(--liquid-text-muted)'}}>
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{ticket.date}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock size={14} />
                        <span>{ticket.time}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPin size={14} />
                        <span>{ticket.venue}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadTicket(ticket.id)}
                    >
                      <Download size={16} className="mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Empty State */
            <div className="text-center py-16">
              <div className="w-24 h-24 liquid-glass rounded-full flex items-center justify-center mx-auto mb-6">
                <Download size={32} className="liquid-glow" style={{color: 'var(--liquid-text-muted)'}} />
              </div>
              <h3 className="text-2xl font-bold mb-4 liquid-glow" style={{color: 'var(--liquid-text-primary)'}}>
                {searchQuery || statusFilter !== 'all' || dateFilter !== 'all' ? 'No tickets found' : 'No tickets yet'}
              </h3>
              <p className="mb-8 max-w-md mx-auto" style={{color: 'var(--liquid-text-muted)'}}>
                {searchQuery || statusFilter !== 'all' || dateFilter !== 'all' 
                  ? 'Try adjusting your filters or search query.'
                  : 'Purchase tickets to events to see them here.'}
              </p>
              {(!searchQuery && statusFilter === 'all' && dateFilter === 'all') && (
                <Button onClick={() => navigate('/')}>Browse Events</Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Tickets;
